import { Link } from "wouter";
import { Menu, X, MessageCircle, Phone } from "lucide-react";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export function Navbar({ forceScrolled = false }: { forceScrolled?: boolean }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const logoUrl = "https://www.thecornwallseoco.co.uk/images/The%20Cornwall%20SEO%20Co.png";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const effectiveIsScrolled = isScrolled || forceScrolled;

  const navLinks = [
    { name: "Home", href: "/" },
    { name: "About", href: "/company.html" },
    { name: "SEO", href: "/search-engine-optimisation.html" },
    { name: "AI", href: "/ai-seo.html" },
    { name: "PPC", href: "/pay-per-click-management.html" },
    { name: "Social Media", href: "/social-media-management.html" },
    { name: "Web Design", href: "/web-design.html" },
    { name: "Contact", href: "/contact.html" },
  ];

  return (
    <nav
      className={cn(
        "fixed top-0 w-full z-50 transition-all duration-300",
        effectiveIsScrolled
          ? "bg-white/95 backdrop-blur-md shadow-sm border-b border-border py-3"
          : "bg-transparent py-5"
      )}
    >
      <div className="container mx-auto px-6 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-2">
            <img 
              src={logoUrl} 
              alt="The Cornwall SEO Co" 
              width={200}
              height={64}
              className={cn(
                "h-12 md:h-16 w-auto object-contain shrink-0",
                effectiveIsScrolled ? "" : "brightness-0 invert"
              )}
            />
        </Link>

        <div className="hidden xl:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.name}
              href={link.href}
              className={cn(
                "text-sm font-bold uppercase tracking-wide transition-colors hover:text-primary",
                effectiveIsScrolled ? "text-foreground" : "text-white hover:text-primary"
              )}
            >
              {link.name}
            </a>
          ))}
          <Link 
            href="/testimonials.html"
            className={cn(
              "font-bold uppercase tracking-wider rounded-none px-6 py-2 cursor-pointer",
              effectiveIsScrolled ? "bg-primary text-black hover:bg-black hover:text-white" : "bg-primary text-black hover:bg-white hover:text-black"
            )}
          >
            Testimonials
          </Link>
          <a
            href="tel:0172674161"
            className={cn(
              "flex items-center gap-2 font-bold uppercase tracking-wider rounded-none px-4 py-2 transition-colors",
              effectiveIsScrolled ? "text-foreground hover:text-primary" : "text-white hover:text-primary"
            )}
            title="Call us"
          >
            <Phone size={20} />
            <span className="text-sm">01726 74161</span>
          </a>
          <a
            href="https://wa.me/447792646061"
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "flex items-center gap-2 font-bold uppercase tracking-wider rounded-none px-4 py-2 transition-colors",
              effectiveIsScrolled ? "text-foreground hover:text-primary" : "text-white hover:text-primary"
            )}
            title="Message us on WhatsApp"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" className="w-5 h-5 fill-current">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.405-.883-.733-1.48-1.638-1.653-1.935-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
            </svg>
          </a>
        </div>

        <button
          className="xl:hidden p-2 text-primary"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="mobile menu"
        >
          {isOpen ? <X className={effectiveIsScrolled ? "text-foreground" : "text-white"} /> : <Menu className={effectiveIsScrolled ? "text-foreground" : "text-white"} />}
        </button>
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 w-full bg-white border-b border-border shadow-2xl xl:hidden flex flex-col animate-in slide-in-from-top-5 max-h-[calc(100vh-70px)] overflow-y-auto">
          <div className="flex flex-col gap-0">
            <div className="px-3 py-2 border-b border-border/50 flex gap-2">
              <a
                href="tel:0172674161"
                className="flex items-center gap-2 font-bold uppercase text-foreground hover:text-primary flex-1 px-3 py-2 hover:bg-primary/10 transition-colors rounded-sm text-sm"
                onClick={() => setIsOpen(false)}
              >
                <Phone size={18} className="text-primary shrink-0" />
                <span>01726 74161</span>
              </a>
              <a
                href="https://wa.me/447792646061"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 font-bold uppercase text-foreground hover:text-green-500 flex-1 px-3 py-2 hover:bg-green-500/10 transition-colors rounded-sm text-sm"
                onClick={() => setIsOpen(false)}
              >
                <svg viewBox="0 0 24 24" width="18" height="18" className="w-[18px] h-[18px] text-green-500 shrink-0 fill-current">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.405-.883-.733-1.48-1.638-1.653-1.935-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
                </svg>
                <span>WhatsApp</span>
              </a>
            </div>

            <nav className="border-b border-border/50">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="block px-5 py-4 text-sm font-black uppercase text-foreground hover:text-primary hover:bg-primary/5 transition-colors border-b border-border/30 last:border-b-0"
                  onClick={() => setIsOpen(false)}
                >
                  {link.name}
                </a>
              ))}
            </nav>

            <div className="px-3 py-4 bg-gradient-to-r from-primary/5 to-primary/10 border-b border-border/50">
              <Link 
                href="/testimonials.html"
                className="block w-full bg-primary text-black font-black uppercase px-5 py-4 rounded-none hover:bg-black hover:text-white transition-all text-center text-sm shadow-md hover:shadow-lg"
                onClick={() => setIsOpen(false)}
              >
                View Testimonials
              </Link>
            </div>

          </div>
        </div>
      )}
    </nav>
  );
}
